import clsx from 'clsx';
import { Button, TextInput } from '@tremor/react';
import { Check, Circle, Eye, EyeOff } from 'lucide-react';
import { UseFormRegisterReturn } from 'react-hook-form';
import {
  authTremorButtonNoFocusRing,
  authTremorTextInputRootClass,
} from '../authFormTremor';
import type { RegistrationPasswordRequirementId } from '../firebaseAuthSchemas';

interface AuthPasswordFieldProps {
  readonly id: string;
  readonly label: string;
  readonly autoComplete: string;
  readonly disabled: boolean;
  readonly showPassword: boolean;
  readonly onToggleShow: () => void;
  readonly error?: string;
  readonly registration: UseFormRegisterReturn;
  readonly dense?: boolean;
  readonly hint?: string;
  readonly passwordRequirementStates?: ReadonlyArray<{
    id: RegistrationPasswordRequirementId;
    label: string;
    met: boolean;
  }>;
}

export function AuthPasswordField({
  id,
  label,
  autoComplete,
  disabled,
  showPassword,
  onToggleShow,
  error,
  registration,
  dense = false,
  hint,
  passwordRequirementStates,
}: AuthPasswordFieldProps) {
  const errorId = `${id}-error-msg`;
  const requirementsId = `${id}-requirements`;
  const describedBy = error ? errorId : undefined;
  const hasError = !!error;

  return (
    <div>
      <label
        htmlFor={id}
        className={clsx(
          'block text-sm font-medium',
          dense ? 'mb-0.5' : 'mb-1',
          hasError ? 'text-red-800' : 'text-gray-700',
        )}
      >
        {label}
      </label>
      <div className="relative w-full">
        <TextInput
          {...registration}
          id={id}
          type={showPassword ? 'text' : 'password'}
          autoComplete={autoComplete}
          disabled={disabled}
          placeholder=""
          error={false}
          className={clsx(
            authTremorTextInputRootClass(dense, hasError),
            'w-full [&_input]:pr-12',
          )}
          aria-invalid={hasError ? true : undefined}
          aria-describedby={describedBy}
          aria-details={passwordRequirementStates ? requirementsId : undefined}
        />
        <Button
          type="button"
          variant="light"
          color="rose"
          size="xs"
          icon={showPassword ? EyeOff : Eye}
          tabIndex={-1}
          onClick={onToggleShow}
          disabled={disabled}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          className={clsx(
            'absolute right-1 top-1/2 z-10 -translate-y-1/2 !gap-0 [&_p]:!hidden',
            '!h-auto !min-w-0 !rounded-lg !border-0 !bg-transparent !p-2 !shadow-none',
            'text-gray-500 hover:!bg-gray-100 hover:!text-gray-800 disabled:!cursor-not-allowed disabled:!opacity-50',
            authTremorButtonNoFocusRing,
          )}
        >
          {'\u200b'}
        </Button>
      </div>
      {error && (
        <p
          id={errorId}
          className={clsx('text-sm text-red-600', dense ? 'mt-0.5' : 'mt-1')}
          role="alert"
        >
          {error}
        </p>
      )}
      {passwordRequirementStates && (
        <div
          id={requirementsId}
          className={clsx(
            'rounded-lg border border-gray-200 bg-gray-50/80',
            dense ? 'mt-1.5 px-2.5 py-1.5' : 'mt-2 px-3 py-2.5',
          )}
          role="region"
          aria-label="Password requirements"
        >
          <p
            className={clsx('text-xs font-medium text-gray-600', dense ? 'mb-1' : 'mb-2')}
          >
            Your password must have:
          </p>
          <ul className={dense ? 'space-y-1' : 'space-y-1.5'}>
            {passwordRequirementStates.map(({ id: reqId, label: reqLabel, met }) => (
              <li
                key={reqId}
                className={clsx('flex items-start text-xs', dense ? 'gap-1.5' : 'gap-2')}
              >
                {met ? (
                  <Check
                    className={clsx(
                      'shrink-0 text-green-600',
                      dense ? 'mt-px h-3.5 w-3.5' : 'mt-0.5 h-4 w-4',
                    )}
                    strokeWidth={2.5}
                    aria-hidden
                  />
                ) : (
                  <Circle
                    className={clsx(
                      'shrink-0 text-gray-300',
                      dense ? 'mt-px h-3.5 w-3.5' : 'mt-0.5 h-4 w-4',
                    )}
                    strokeWidth={2}
                    aria-hidden
                  />
                )}
                <span
                  className={clsx(
                    'leading-snug',
                    met ? 'text-gray-800' : 'text-gray-600',
                  )}
                >
                  <span className="sr-only">{met ? 'Done: ' : 'Still needed: '}</span>
                  {reqLabel}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {hint && !passwordRequirementStates && (
        <p className={clsx('text-xs text-gray-500', dense ? 'mt-0' : 'mt-0.5')}>{hint}</p>
      )}
    </div>
  );
}
